// ===== Production Socket Manager =====

import io from 'socket.io-client';
import { SOCKET_EVENTS } from '../constants/ChatConstants';

export class SocketManager {
  constructor(options = {}) {
    this.socket = options.socket;
    this.currentUser = options.currentUser;
    this.receiverUserId = options.receiverUserId;
    this.chatRoomId = options.chatRoomId;
    this.logger = options.logger;
    this.config = options.config || {};
    this.autoConnect = options.autoConnect !== false; // Default to true for backward compatibility
    
    // Callbacks
    this.onConnectionChange = options.onConnectionChange || (() => {});
    this.onUserStatusChange = options.onUserStatusChange || (() => {});
    this.onError = options.onError || (() => {});
    this.onMessageReceived = options.onMessageReceived || (() => {});
    this.onMessageHistory = options.onMessageHistory || (() => {});
    this.onIncomingCall = options.onIncomingCall || (() => {});
    this.onCallAnswer = options.onCallAnswer || (() => {});
    this.onIceCandidate = options.onIceCandidate || (() => {});
    this.onCallEnded = options.onCallEnded || (() => {});
    this.onCallRejected = options.onCallRejected || (() => {});
    
    // State
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = this.config.RECONNECTION_ATTEMPTS || 5;
    this.listenersAttached = false;
    
    this.init();
  }

  init() {
    if (!this.socket) {
      if (this.autoConnect) {
        this.createFallbackSocket();
      } else {
        this.logger?.warn('No socket provided and autoConnect is disabled');
      }
    } else {
      this.attachListeners();
      this.isConnected = this.socket.connected;
      this.onConnectionChange(this.isConnected);
    }
  }

  createFallbackSocket() {
    try {
      const possibleUrls = [
        process.env.REACT_APP_SOCKET_URL,
        'http://localhost:5001',
        window.location.origin
      ].filter(Boolean);
      
      const socketUrl = possibleUrls[0];
      this.logger?.info('Creating fallback socket connection', { url: socketUrl });
      
      this.socket = io(socketUrl, {
        transports: this.config.TRANSPORTS || ['polling', 'websocket'],
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.config.RECONNECTION_DELAY || 1000,
        reconnectionDelayMax: this.config.RECONNECTION_DELAY_MAX || 5000,
        timeout: this.config.TIMEOUT || 20000,
        upgrade: true,
        rememberUpgrade: true,
        autoConnect: true,
        withCredentials: false
      });
      
      this.attachListeners();
      
    } catch (error) {
      this.logger?.error('Failed to create fallback socket', error);
      this.onError(error);
    }
  }

  attachListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on(SOCKET_EVENTS.CONNECT, this.handleConnect.bind(this));
    this.socket.on(SOCKET_EVENTS.DISCONNECT, this.handleDisconnect.bind(this));
    this.socket.on(SOCKET_EVENTS.CONNECT_ERROR, this.handleConnectError.bind(this));
    this.socket.on(SOCKET_EVENTS.RECONNECT, this.handleReconnect.bind(this));
    this.socket.on(SOCKET_EVENTS.RECONNECT_ERROR, this.handleReconnectError.bind(this));

    // Message events
    this.socket.on(SOCKET_EVENTS.RECEIVE_DIRECT_MESSAGE, this.handleDirectMessage.bind(this));
    this.socket.on(SOCKET_EVENTS.RECEIVE_MESSAGE, this.handleMessage.bind(this));
    this.socket.on(SOCKET_EVENTS.MESSAGE_HISTORY, this.handleMessageHistory.bind(this));

    // User status events
    this.socket.on(SOCKET_EVENTS.USER_STATUS_RESPONSE, this.handleUserStatus.bind(this));
    this.socket.on(SOCKET_EVENTS.USER_ONLINE, this.handleUserOnline.bind(this));
    this.socket.on(SOCKET_EVENTS.USER_OFFLINE, this.handleUserOffline.bind(this));

    // WebRTC events
    this.socket.on(SOCKET_EVENTS.WEBRTC_CALL_OFFER, this.handleCallOffer.bind(this));
    this.socket.on(SOCKET_EVENTS.WEBRTC_CALL_ANSWER, this.handleCallAnswer.bind(this));
    this.socket.on(SOCKET_EVENTS.WEBRTC_ICE_CANDIDATE, this.handleIceCandidate.bind(this));
    this.socket.on(SOCKET_EVENTS.WEBRTC_CALL_ENDED, this.handleCallEnded.bind(this));
    this.socket.on(SOCKET_EVENTS.WEBRTC_CALL_FAILED, this.handleCallFailed.bind(this));
    this.socket.on(SOCKET_EVENTS.WEBRTC_CALL_REJECTED, this.handleCallRejected.bind(this));

    // Error handling
    this.socket.on('error', this.handleError.bind(this));
  }

  connect() {
    if (!this.socket) {
      if (this.autoConnect) {
        this.createFallbackSocket();
      } else {
        this.logger?.warn('No socket provided and autoConnect is disabled');
        this.onConnectionChange(false);
        return;
      }
    }

    // Handle socket connection
    if (!this.socket.connected) {
      if (this.autoConnect) {
        this.logger?.info('Socket not connected, attempting to connect (autoConnect enabled)');
        this.socket.connect();
      } else {
        // If autoConnect is false but socket is disconnected, try to reconnect anyway
        // This handles cases where the socket was passed but lost connection
        this.logger?.info('Socket not connected, attempting to reconnect existing socket');
        this.socket.connect();
      }
    } else {
      this.logger?.info('Using existing connected socket');
      // Update connection state immediately for already connected socket
      this.isConnected = true;
      this.onConnectionChange(true);
      
      // Immediately handle chat setup for connected socket
      this.handleChatSetup();
    }

    // Listen for connection events to handle chat setup when socket connects
    if (!this.socket.connected) {
      const connectHandler = () => {
        this.logger?.info('Socket connected, setting up chat');
        this.handleChatSetup();
        this.socket.off('connect', connectHandler); // Remove this handler after first connection
      };
      this.socket.on('connect', connectHandler);
    }
  }

  handleChatSetup() {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot setup chat - socket not connected');
      return;
    }

    // Always get message history for this specific chat
    this.socket.emit(SOCKET_EVENTS.GET_MESSAGE_HISTORY, {
      userId1: this.currentUser.socketId,
      userId2: this.receiverUserId
    });

    // Always check receiver status for this specific chat
    this.socket.emit(SOCKET_EVENTS.CHECK_USER_STATUS, {
      userId: this.receiverUserId
    });

    // If autoConnect is enabled, also join rooms (for fallback socket)
    if (this.autoConnect) {
      // Join user-specific room and register with server
      this.logger?.info('Registering user with server', { 
        userId: this.currentUser.socketId, 
        role: this.currentUser.role 
      });
      
      if (this.currentUser?.role === 'doctor') {
        this.socket.emit('join_doctor', { userId: this.currentUser.socketId });
      } else if (this.currentUser?.role === 'patient') {
        this.socket.emit('join_patient', { userId: this.currentUser.socketId });
      } else {
        // Default connection for users without specific roles
        this.socket.emit('join_patient', { userId: this.currentUser.socketId });
      }

      // Also emit user-connect for backward compatibility
      this.socket.emit('user-connect', {
        userId: this.currentUser.socketId
      });
    } else {
      this.logger?.info('Using existing socket connection from login - rooms already joined');
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  reconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.logger?.info('Attempting to reconnect', { attempt: this.reconnectAttempts });
      
      if (this.socket) {
        this.socket.connect();
      } else {
        this.createFallbackSocket();
      }
    } else {
      this.logger?.error('Max reconnection attempts reached');
      this.onError(new Error('Max reconnection attempts reached'));
    }
  }

  // Event handlers
  handleConnect() {
    this.logger?.info('Socket connected');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.onConnectionChange(true);
  }

  handleDisconnect(reason) {
    this.logger?.info('Socket disconnected', { reason });
    this.isConnected = false;
    this.onConnectionChange(false);
  }

  handleConnectError(error) {
    this.logger?.error('Socket connection error', error);
    this.onError(error);
  }

  handleReconnect() {
    this.logger?.info('Socket reconnected');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.onConnectionChange(true);
  }

  handleReconnectError(error) {
    this.logger?.error('Socket reconnection error', error);
    this.onError(error);
  }

  handleDirectMessage(message) {
    if (this.onMessageReceived) { this.onMessageReceived(message); }
    this.logger?.debug('Direct message received', message);
  }

  handleMessage(message) {
    if (this.onMessageReceived) { this.onMessageReceived(message); }
    this.logger?.debug('Message received', message);
  }

  handleMessageHistory(messages) {
    this.logger?.info('Message history received', { count: messages?.length });
    if (this.onMessageHistory) {
      this.onMessageHistory(messages || []);
    }
  }

  handleUserStatus(data) {
    this.logger?.debug('User status received', data);
    if (data.userId === this.receiverUserId) {
      this.onUserStatusChange(data.isOnline);
    }
  }

  handleUserOnline(userId) {
    this.logger?.debug('User came online', { userId });
    if (userId === this.receiverUserId) {
      this.onUserStatusChange(true);
    }
  }

  handleUserOffline(userId) {
    this.logger?.debug('User went offline', { userId });
    if (userId === this.receiverUserId) {
      this.onUserStatusChange(false);
    }
  }

  handleCallOffer(data) {
    this.logger?.info('Call offer received', data);
    if (this.onIncomingCall) {
      this.onIncomingCall({
        callerId: data.callerId,
        callerName: data.callerName || 'Unknown',
        callType: data.callType || 'audio',
        sessionId: data.sessionId,
        offer: data.offer
      });
    }
  }

  handleCallAnswer(data) {
    this.logger?.info('Call answer received', data);
    if (this.onCallAnswer) {
      this.onCallAnswer(data);
    }
  }

  handleIceCandidate(data) {
    this.logger?.debug('ICE candidate received', data);
    if (this.onIceCandidate) {
      this.onIceCandidate(data);
    }
  }

  handleCallEnded(data) {
    this.logger?.info('Call ended by remote peer', data);
    if (this.onCallEnded) {
      this.onCallEnded(data);
    }
  }

  handleCallFailed(data) {
    this.logger?.error('Call failed', data);
    if (this.onCallEnded) {
      this.onCallEnded({ ...data, reason: 'failed' });
    }
  }

  handleCallRejected(data) {
    this.logger?.info('Call rejected', data);
    if (this.onCallRejected) {
      this.onCallRejected(data);
    }
  }

  handleError(error) {
    this.logger?.error('Socket error', error);
    this.onError(error);
  }

  // Message sending
  sendMessage(message) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot send message - socket not connected');
      return false;
    }

    try {
      // Enhanced logging for debugging message types
      this.logger?.info('Sending message via socket', { 
        id: message.id, 
        type: message.type, 
        sender: message.sender, 
        receiver: message.receiver,
        hasUrl: !!message.url,
        hasFileName: !!message.fileName
      });
      
      this.socket.emit(SOCKET_EVENTS.SEND_DIRECT_MESSAGE, message);
      this.logger?.debug('Message sent via socket', message);
      return true;
    } catch (error) {
      this.logger?.error('Failed to send message', error);
      return false;
    }
  }

  // Call signaling
  async sendCallOffer(offerData) {
    if (!this.socket || !this.socket.connected) {
      throw new Error('Socket not connected');
    }

    try {
      this.logger?.info('Sending call offer', offerData);
      this.socket.emit(SOCKET_EVENTS.WEBRTC_CALL_OFFER, offerData);
      this.logger?.info('Call offer sent successfully');
      return true;
    } catch (error) {
      this.logger?.error('Failed to send call offer', error);
      throw error;
    }
  }

  sendCallAnswer(answerData) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot send call answer - socket not connected');
      return false;
    }

    try {
      this.socket.emit(SOCKET_EVENTS.WEBRTC_CALL_ANSWER, answerData);
      this.logger?.debug('Call answer sent', answerData);
      return true;
    } catch (error) {
      this.logger?.error('Failed to send call answer', error);
      return false;
    }
  }

  sendIceCandidate(candidateData) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot send ICE candidate - socket not connected');
      return false;
    }

    try {
      this.socket.emit(SOCKET_EVENTS.WEBRTC_ICE_CANDIDATE, candidateData);
      this.logger?.debug('ICE candidate sent', candidateData);
      return true;
    } catch (error) {
      this.logger?.error('Failed to send ICE candidate', error);
      return false;
    }
  }

  rejectCall(callerId, sessionId) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot reject call - socket not connected');
      return false;
    }

    try {
      this.socket.emit(SOCKET_EVENTS.WEBRTC_CALL_REJECTED, {
        receiverId: callerId,
        sessionId
      });
      this.logger?.info('Call rejected', { callerId, sessionId });
      return true;
    } catch (error) {
      this.logger?.error('Failed to reject call', error);
      return false;
    }
  }

  endCall(receiverId, sessionId) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot end call - socket not connected');
      return false;
    }

    try {
      this.socket.emit(SOCKET_EVENTS.WEBRTC_CALL_ENDED, {
        receiverId,
        sessionId
      });
      this.logger?.info('Call ended notification sent', { receiverId, sessionId });
      return true;
    } catch (error) {
      this.logger?.error('Failed to send call end notification', error);
      return false;
    }
  }

  // Request chat history for specific users
  requestChatHistory(userId1, userId2) {
    if (!this.socket || !this.socket.connected) {
      this.logger?.warn('Cannot request chat history - socket not connected');
      return false;
    }

    try {
      this.logger?.info('Requesting chat history', { userId1, userId2 });
      this.socket.emit(SOCKET_EVENTS.GET_MESSAGE_HISTORY, {
        userId1: userId1,
        userId2: userId2
      });
      return true;
    } catch (error) {
      this.logger?.error('Failed to request chat history', error);
      return false;
    }
  }

  // Utility methods
  isSocketConnected() {
    return this.socket && this.socket.connected;
  }

  getSocketId() {
    return this.socket?.id;
  }

  cleanup() {
    if (this.socket) {
      // Only remove listeners that were added by this manager
      // Don't remove all listeners as other components might be using the same socket
      this.socket.off(SOCKET_EVENTS.CONNECT, this.handleConnect.bind(this));
      this.socket.off(SOCKET_EVENTS.DISCONNECT, this.handleDisconnect.bind(this));
      this.socket.off(SOCKET_EVENTS.CONNECT_ERROR, this.handleConnectError.bind(this));
      this.socket.off(SOCKET_EVENTS.RECONNECT, this.handleReconnect.bind(this));
      this.socket.off(SOCKET_EVENTS.RECONNECT_ERROR, this.handleReconnectError.bind(this));
      this.socket.off(SOCKET_EVENTS.RECEIVE_DIRECT_MESSAGE, this.handleDirectMessage.bind(this));
      this.socket.off(SOCKET_EVENTS.RECEIVE_MESSAGE, this.handleMessage.bind(this));
      this.socket.off(SOCKET_EVENTS.MESSAGE_HISTORY, this.handleMessageHistory.bind(this));
      this.socket.off(SOCKET_EVENTS.USER_STATUS_RESPONSE, this.handleUserStatus.bind(this));
      this.socket.off(SOCKET_EVENTS.USER_ONLINE, this.handleUserOnline.bind(this));
      this.socket.off(SOCKET_EVENTS.USER_OFFLINE, this.handleUserOffline.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_CALL_OFFER, this.handleCallOffer.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_CALL_ANSWER, this.handleCallAnswer.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_ICE_CANDIDATE, this.handleIceCandidate.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_CALL_ENDED, this.handleCallEnded.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_CALL_FAILED, this.handleCallFailed.bind(this));
      this.socket.off(SOCKET_EVENTS.WEBRTC_CALL_REJECTED, this.handleCallRejected.bind(this));
      this.socket.off('error', this.handleError.bind(this));
      
      // Only disconnect if this manager created the socket (autoConnect was true)
      // Don't disconnect shared socket instances from App.js
      if (this.autoConnect && this.socket.disconnect) {
        this.logger?.info('Disconnecting socket created by SocketManager');
        this.socket.disconnect();
      } else {
        this.logger?.info('Preserving shared socket connection from login');
      }
      
      this.socket = null;
    }
  }
}

export default SocketManager;
